import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { GenericListComponent } from '../../shared/generic-list/generic-list.component';
import { AppUser } from '../../../models/appUser';
import { AppUserRole } from '../../../models/enums/appUserRole.enum';
import { Category } from '../../../models/enums/category.enum';

const CategoryDisplayNames: { [key: number]: string } = {
  [Category.None]: 'None',
  [Category.Audit]: 'Audit',
  [Category.CorporateGovernance]: 'Corporate governance',
  [Category.Csr]: 'ESG',
  [Category.VotingResults]: 'Voting results',
  [Category.Compensation]: 'Compensation',
  [Category.Accounting]: 'Audit'
};
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { ColumnDefinition } from '../../../models/columnDefinition';
import { UserService } from '../../../services/user.service';
import { UpdateUserRequest } from '../../../models/api/updateUserRequest';
import { UserSearchBoxComponent } from '../user-search-box/user-search-box.component';
import { UserSearchFilter } from '../../../models/userSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { CurrentUserState } from '../../../state/current-user.state';

@Component({
  selector: 'app-user-list',
  imports: [
    CommonModule,
    GenericListComponent,
    UserSearchBoxComponent,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    NgxSpinnerModule
  ],
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss']
})
export class UserListComponent implements OnInit {
  @ViewChild(GenericListComponent) listComponent?: GenericListComponent<AppUser>;

  users: AppUser[] = [];
  isSearchVisible = false;
  totalItems = 0;
  pageSize = 50;
  pageNumber = 1;
  sortBy = 'UserName';
  ascending = true;
  selectedUsers: AppUser[] = [];
  isDeveloper = false;

  columnDefinitions: ColumnDefinition[] = [
    { propertyName: 'userName', displayName: 'User Name' },
    { propertyName: 'fullName', displayName: 'Full Name' },
    { propertyName: 'role', displayName: 'Role', dataType: 'enum', enumType: AppUserRole },
    { propertyName: 'category', displayName: 'Category', dataType: 'enum', enumType: CategoryDisplayNames },
    { propertyName: 'isActive', displayName: 'Active', dataType: 'boolean' },
    // Not sortable: lastLogin is derived from login-attempt history, not a sortable AppUser column.
    { propertyName: 'lastLogin', displayName: 'Last Login', dataType: 'datetime', sortable: false }
  ]

  constructor(
    private userService: UserService,
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private confirmationDialogService: ConfirmationDialogService,
    private toast: HotToastService,
    private currentUserState: CurrentUserState
  ) {}

  ngOnInit(): void {
    this.isDeveloper = this.currentUserState.isDeveloper();
    this.loadUsers();
  }

  loadUsers(): void {
    this.spinnerService.show();
    this.userService.getUsers(this.pageNumber, this.pageSize, this.sortBy, this.ascending).pipe(
      map(response => {
        this.totalItems = response.totalCount;
        this.spinnerService.hide();
        return response.data;
      }),
      catchError(error => {
        console.error('Error loading users:', error);
        this.spinnerService.hide();
        return of([]);
      })
    ).subscribe(users => {
      this.users = users;
    });
  }

  onCreateUser(): void {
    this.router.navigate(['/users/create']);
  }

  onSortChange(sort: Sort): void {
    if (sort.direction) {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
    } else {
      this.sortBy = 'Id';
      this.ascending = true;
    }
    this.loadUsers();
  }

  onPageChange(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadUsers();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onUserSearchFilterChange(userSearchFilter: UserSearchFilter): void {
    this.loadUsers();
  }

  onEditClick(user: AppUser): void {
    this.router.navigate(['/users/edit', user.id]);
  }

  /** Opens the audit log of changes made BY this user, in a new tab. */
  onViewAuditLog(user: AppUser): void {
    window.open(`/auditlog?changedBy=${encodeURIComponent(user.userName)}`, '_blank', 'noopener');
  }

  onSelectionChange(selected: AppUser[]): void {
    this.selectedUsers = selected;
  }

  onBulkDelete(): void {
    if (this.selectedUsers.length === 0) return;

    const count = this.selectedUsers.length;
    this.confirmationDialogService.openConfirmationDialog(
      'Delete Users',
      `Are you sure you want to delete ${count} user${count === 1 ? '' : 's'}?`,
      'Delete',
      'Cancel'
    ).subscribe(result => {
      if (!result) return;

      const ids = this.selectedUsers.map(u => u.id);
      this.spinnerService.show();
      this.userService.deleteUsers(ids).subscribe({
        next: (response) => {
          this.spinnerService.hide();
          this.listComponent?.clearSelection();
          this.selectedUsers = [];
          if (response.failed.length === 0) {
            this.toast.success(`${response.deleted.length} user${response.deleted.length === 1 ? '' : 's'} deleted`, {
              iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
            });
          } else {
            this.toast.warning(
              `Deleted ${response.deleted.length} of ${ids.length}. ${response.failed.length} failed.`
            );
          }
          this.loadUsers();
        },
        error: (error) => {
          console.error('Error during bulk delete:', error);
          this.spinnerService.hide();
          this.toast.error('Failed to delete users');
        }
      });
    });
  }

  onDeleteClick(user: AppUser): void {
    this.confirmationDialogService.openConfirmationDialog(
      'Delete User',
      `Are you sure you want to delete user "${user.userName}"?`,
      'Delete',
      'Cancel'
    ).subscribe(result => {
      if (result) {
        this.spinnerService.show();
        this.userService.deleteUser(user.id).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('User deleted successfully', {
              iconTheme: { primary: 'var(--mat-sys-primary)', secondary: '#FFF' }
            });
            this.loadUsers();
          },
          error: (error) => {
            console.error('Error deleting user:', error);
            this.spinnerService.hide();
            this.toast.error('Failed to delete user');
          }
        });
      }
    });
  }

  onItemSave(event: {item: AppUser, newValues: {[key: string]: any}}): void {
    const user = event.item;
    const updatedUser: UpdateUserRequest = {
      email: event.newValues['email'] ?? user.email,
      fullName: event.newValues['fullName'] ?? user.fullName,
      role: event.newValues['role'] ?? user.role,
      category: event.newValues['category'] ?? user.category,
      companyAccess: event.newValues['companyAccess'] ?? user.companyAccess,
      // Carried forward, not edited from the list — omitting it would clear the user's
      // read-only tabs on every inline edit.
      companyAccessReadOnly: event.newValues['companyAccessReadOnly'] ?? user.companyAccessReadOnly ?? [],
      accessToYears: event.newValues['accessToYears'] ?? user.accessToYears,
      isActive: event.newValues['isActive'] ?? user.isActive
    };

    this.spinnerService.show();
    this.userService.updateUser(user.id, updatedUser).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.loadUsers();
      },
      error: (error) => {
        console.error('Error updating user:', error);
        this.spinnerService.hide();
      }
    });
  }
}
